if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.variants = null;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.loadVariants();
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          this.updateOptionsAvailability();
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
        // Initialize option availability on load
        setTimeout(() => this.updateOptionsAvailability(), 100);
      }

      loadVariants() {
        // Get all variants from the section script
        const script = this.querySelector('[type="application/json"][data-product]');
        if (script) {
          try {
            const productData = JSON.parse(script.textContent);
            this.variants = productData.variants || [];
          } catch (e) {
            console.error('Failed to parse product data:', e);
          }
        }
      }

      updateOptionsAvailability() {
        if (!this.variants || this.variants.length === 0) return;

        // Get currently selected values by option position
        const selectedValuesByPosition = {};
        this.querySelectorAll('fieldset input:checked, select').forEach((element) => {
          const value = element.tagName === 'SELECT' ? element.value : element.value;
          // Extract position from name (e.g., "Option1-1" -> position 1)
          const match = element.name.match(/(\d+)$/);
          if (match) {
            const position = parseInt(match[1], 10);
            selectedValuesByPosition[position] = value;
          }
        });

        // Get all option inputs
        const allInputs = this.querySelectorAll('fieldset input, select option');

        // Update availability for each option
        allInputs.forEach((input) => {
          // Extract position from name
          const inputName = input.tagName === 'OPTION' ? input.parentElement.name : input.name;
          const match = inputName.match(/(\d+)$/);
          if (!match) return;

          const position = parseInt(match[1], 10);
          const optionValue = input.tagName === 'OPTION' ? input.value : input.value;

          // Skip if this is the currently selected option
          if (selectedValuesByPosition[position] === optionValue) return;

          // Check if this option forms a valid combination with other selected options
          const isValid = this.variants.some((variant) => {
            const variantOptions = variant.options || [];

            // Check if this option matches
            if (variantOptions[position - 1] !== optionValue) return false;

            // Check if all other selected options match
            for (const [pos, selectedValue] of Object.entries(selectedValuesByPosition)) {
              const positionIndex = parseInt(pos, 10) - 1;
              if (variantOptions[positionIndex] !== selectedValue) return false;
            }

            return true;
          });

          // Update disabled state
          if (input.tagName === 'OPTION') {
            // For select options, we can't easily disable specific options without affecting the UI
            // Instead, we'll mark them with a data attribute
            input.dataset.unavailable = isValid ? 'false' : 'true';
          } else {
            // For radio inputs, add/remove disabled class
            if (isValid) {
              input.classList.remove('disabled');
              input.disabled = false;
            } else {
              input.classList.add('disabled');
              input.disabled = true;
            }
          }
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
